import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

const Line = ({ children, index }) => (
    <div className="overflow-hidden">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, ease: EASE, delay: 0.2 + index * 0.14 }}
        >
            {children}
        </motion.span>
    </div>
);

export default function Hero() {
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12 max-w-7xl mx-auto">
            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-xs font-bold tracking-[0.25em] uppercase text-fern mb-8"
                data-testid="hero-overline"
            >
                Commercial & Facilities Cleaning
            </motion.p>

            <h1 className="font-serif font-medium tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-8xl text-forest max-w-5xl">
                <Line index={0}>Cleaning your business</Line>
                <Line index={1}>
                    <em className="font-normal">can rely on.</em>
                </Line>
            </h1>

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
                className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-10"
            >
                <p className="text-base font-light leading-relaxed tracking-wide text-fern max-w-md">
                    The Zami Concept delivers dependable commercial cleaning that keeps your premises
                    clean, hygienic, and presentable, so your team can focus on the work that matters.
                </p>
                <div className="flex items-center gap-4">
                    <button
                        data-testid="hero-cta-quote-button"
                        onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                        className="group rounded-full bg-forest text-background text-xs font-bold tracking-[0.15em] uppercase px-8 py-4 flex items-center gap-2 hover:-translate-y-1 transition-transform duration-300"
                    >
                        Request a quote
                        <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                    </button>
                    <button
                        data-testid="hero-cta-services-button"
                        onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
                        className="rounded-full border border-forest text-forest text-xs font-bold tracking-[0.15em] uppercase px-8 py-4 hover:bg-sage transition-colors duration-300"
                    >
                        Our services
                    </button>
                </div>
            </motion.div>

            <motion.div
                ref={imgRef}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.1, ease: EASE, delay: 0.9 }}
                className="spotlight-frame mt-14 md:mt-20"
                data-testid="hero-image-frame"
            >
                <div className="overflow-hidden rounded-[1.1rem] h-[46vh] md:h-[62vh]">
                    <motion.img
                        src="https://images.unsplash.com/photo-1594235046256-90e5572fd5f9?auto=format&fit=crop&w=2000&q=80"
                        alt="A bright modern office with greenery, cleaned by The Zami Concept"
                        style={{ y, scale: 1.15 }}
                        className="w-full h-full object-cover"
                    />
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="mt-6 flex items-center gap-2 text-fern"
            >
                <ArrowDown className="w-4 h-4 animate-bounce" />
                <span className="text-xs font-bold tracking-[0.2em] uppercase">Scroll</span>
            </motion.div>
        </section>
    );
}
